import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

/* ==========================================================================
   Button Component
   Based on design.md specifications
   Task F30: Updated to use pharmacy-primary CSS variables for white-labeling
   ========================================================================== */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive'
  /** Button size */
  size?: 'sm' | 'md' | 'lg'
  /** Loading state - shows spinner, disables interaction */
  loading?: boolean
  /** Button content */
  children: ReactNode
}

/**
 * Button component with multiple variants and sizes.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">Submit</Button>
 * <Button variant="secondary" loading>Saving...</Button>
 * <Button variant="ghost" size="sm">Edit</Button>
 * <Button variant="destructive">Delete</Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    const baseStyles = [
      // Base layout and typography
      'inline-flex items-center justify-center',
      'font-semibold leading-tight',
      'rounded-md',
      'transition-all duration-150 ease-in-out',
      // Focus ring using pharmacy secondary color (lavender by default)
      'focus:outline-none focus-visible:ring-3 focus-visible:ring-[var(--pharmacy-secondary)]',
      // Subtle scale on press
      'active:scale-[0.98]',
      // Cursor
      isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
    ]

    const variantStyles: Record<string, string[]> = {
      primary: [
        // Use pharmacy-primary for white-labeling (Task F30)
        'bg-[var(--pharmacy-primary)] text-white',
        'hover:bg-[var(--pharmacy-primary-hover)]',
        'disabled:bg-[var(--color-gray-400)] disabled:text-white',
      ],
      secondary: [
        // Use pharmacy-primary for white-labeling (Task F30)
        'bg-white text-[var(--pharmacy-primary)]',
        'border border-[var(--pharmacy-primary)]',
        'hover:bg-[var(--color-soft-lavender)]',
        'disabled:border-[var(--color-gray-400)] disabled:text-[var(--color-gray-400)] disabled:bg-white',
      ],
      ghost: [
        // Use pharmacy-primary for white-labeling (Task F30)
        'bg-transparent text-[var(--pharmacy-primary)]',
        'hover:underline',
        'disabled:text-[var(--color-gray-400)] disabled:no-underline',
      ],
      destructive: [
        // Destructive keeps semantic error color (not customizable per design.md)
        'bg-[var(--color-error)] text-white',
        'hover:bg-[#A31F1F]', // 10% darker than #C62828
        'disabled:bg-[var(--color-gray-400)] disabled:text-white',
      ],
    }

    const sizeStyles: Record<string, string[]> = {
      sm: ['px-3 py-2 text-[13px]'],
      md: ['px-5 py-3 text-sm'],
      lg: ['px-6 py-4 text-base'],
    }

    const classes = [
      ...baseStyles,
      ...variantStyles[variant],
      ...sizeStyles[size],
      // Position relative for loading spinner
      loading && 'relative',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={classes}
        aria-busy={loading}
        {...props}
      >
        {/* Content - hidden when loading */}
        <span
          className={`inline-flex items-center${loading ? ' invisible' : ''}`}
        >
          {children}
        </span>

        {/* Loading spinner - centered over button */}
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner size={size} variant={variant} />
          </span>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

/* ==========================================================================
   Spinner Component (internal)
   ========================================================================== */

interface SpinnerProps {
  size: 'sm' | 'md' | 'lg'
  variant: 'primary' | 'secondary' | 'ghost' | 'destructive'
}

function Spinner({ size, variant }: SpinnerProps) {
  const sizeMap = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  // Spinner color based on variant (contrast with background)
  // Use pharmacy-primary for white-labeling (Task F30)
  const colorClass =
    variant === 'secondary' || variant === 'ghost'
      ? 'border-[var(--pharmacy-primary)]'
      : 'border-white'

  return (
    <span
      className={`
        ${sizeMap[size]}
        ${colorClass}
        inline-block
        border-2
        border-t-transparent
        rounded-full
        animate-spin
      `}
      aria-hidden="true"
    />
  )
}

export default Button
